import type { ReactNode } from 'react';

import type { ApiKey } from '@/account/api-keys';
import { Button } from '@/components/Button/Button';
import { KeyStatus } from '@/components/KeyStatus/KeyStatus';
import { formatDate, formatRelative } from '@/lib/format';
import { useMediaQuery } from '@/lib/useMediaQuery';

import styles from './ApiKeyList.module.css';

export interface ApiKeyListProps {
  keys: ApiKey[];
  onRegenerate: (key: ApiKey) => void;
  onRevoke: (key: ApiKey) => void;
  /** The key's name, rendered as a link to its page. Routing is the caller's. */
  renderName?: (key: ApiKey) => ReactNode;
  /** Shown above the keys, e.g. why no more can be created. */
  notice?: ReactNode;
}

const NARROW = '(max-width: 640px)';

function nameOf(key: ApiKey): string {
  return key.name || 'Untitled key';
}

function KeyTable({
  keys,
  renderName,
  actions,
  label,
}: {
  keys: ApiKey[];
  renderName: (key: ApiKey) => ReactNode;
  actions?: (key: ApiKey, compact: boolean) => ReactNode;
  label: string;
}) {
  const narrow = useMediaQuery(NARROW);

  if (narrow) {
    return (
      <ul className={styles.cards} aria-label={label}>
        {keys.map((key) => (
          <li key={key.id} className={styles.card}>
            <div className={styles.cardTop}>
              <span className={styles.name}>{renderName(key)}</span>
              {actions ? <span className={styles.cardActions}>{actions(key, true)}</span> : null}
            </div>
            <div className={styles.cardMeta}>
              <code className={styles.prefix}>{key.secretPrefix}…</code>
              {key.createdAt ? <span>Created {formatDate(key.createdAt)}</span> : null}
              <span>
                {actions
                  ? `Used ${key.lastUsedAt ? formatRelative(key.lastUsedAt) : 'never'}`
                  : key.revokedAt
                    ? `Revoked ${formatDate(key.revokedAt)}`
                    : null}
              </span>
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={styles.scroll}>
      <table className={styles.table} aria-label={label}>
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Key</th>
            <th scope="col">Created</th>
            <th scope="col">{actions ? 'Last used' : 'Revoked'}</th>
            {actions ? (
              <th scope="col">
                <span className={styles.visuallyHidden}>Actions</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => (
            <tr key={key.id}>
              <th scope="row" className={styles.name}>
                {renderName(key)}
              </th>
              <td>
                <code className={styles.prefix}>{key.secretPrefix}…</code>
              </td>
              <td className={styles.muted} data-label="Created">
                {key.createdAt ? formatDate(key.createdAt) : '—'}
              </td>
              <td className={styles.muted} data-label={actions ? 'Last used' : 'Revoked'}>
                {actions
                  ? key.lastUsedAt
                    ? formatRelative(key.lastUsedAt)
                    : 'Never'
                  : key.revokedAt
                    ? formatDate(key.revokedAt)
                    : '—'}
              </td>
              {actions ? <td className={styles.actions}>{actions(key, false)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The organization's keys: active ones to manage, revoked ones for the record. */
export function ApiKeyList({
  keys,
  onRegenerate,
  onRevoke,
  renderName = (key) => nameOf(key),
  notice,
}: ApiKeyListProps) {
  const active = keys.filter((key) => !key.revokedAt);
  const revoked = keys.filter((key) => key.revokedAt);

  return (
    <div className={styles.list}>
      {notice}
      <section className={styles.section} aria-labelledby="active-keys-heading">
        <h3 id="active-keys-heading" className={styles.sectionTitle}>
          Active keys
        </h3>
        {active.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>No active keys</p>
            <p className={styles.muted}>Create a key to start calling the API.</p>
          </div>
        ) : (
          <KeyTable
            label="Active keys"
            keys={active}
            renderName={(key) => (
              <>
                {renderName(key)} <KeyStatus revoked={false} />
              </>
            )}
            actions={(key, compact) =>
              compact ? (
                <>
                  <button
                    type="button"
                    className={styles.textAction}
                    aria-label={`Regenerate ${nameOf(key)}`}
                    onClick={() => onRegenerate(key)}
                  >
                    Regenerate
                  </button>
                  <button
                    type="button"
                    className={`${styles.textAction} ${styles.revoke}`}
                    aria-label={`Revoke ${nameOf(key)}`}
                    onClick={() => onRevoke(key)}
                  >
                    Revoke
                  </button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    aria-label={`Regenerate ${nameOf(key)}`}
                    onClick={() => onRegenerate(key)}
                  >
                    Regenerate
                  </Button>
                  <Button
                    variant="ghost"
                    className={styles.revoke}
                    aria-label={`Revoke ${nameOf(key)}`}
                    onClick={() => onRevoke(key)}
                  >
                    Revoke
                  </Button>
                </>
              )
            }
          />
        )}
      </section>

      {revoked.length > 0 ? (
        <details className={`${styles.section} ${styles.revokedGroup}`}>
          <summary>Revoked keys ({revoked.length})</summary>
          <KeyTable label="Revoked keys" keys={revoked} renderName={renderName} />
        </details>
      ) : null}
    </div>
  );
}
